import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { StyleSheet, View, Pressable } from "react-native";
import { CopyIcon, CheckIcon } from "@phosphor-icons/react";
import { colors, radii, spacing } from "../../../../styles/design-system";
import useCopyContent from "../../../../hooks/useCopyContent";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import Tooltip from "../../../common/tooltip/Tooltip";
import { ReadAloudPlaybackControl, ReadAloudIndicator } from "./ReadAloudPlaybackControl";
import type { ReadAloudControl } from "./ReadAloudPlaybackControl";
import { AnimatePresence, MotiView } from "moti";
import { ThumbsUpIcon, ThumbsDownIcon } from "@phosphor-icons/react";

const READ_ALOUD_WORDS_PER_MINUTE = 150;
const READ_ALOUD_MIN_SECONDS = 3;

const estimateTotalSeconds = (content: string) => {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  const estimated = Math.ceil(words / (READ_ALOUD_WORDS_PER_MINUTE / 60));
  return Math.max(READ_ALOUD_MIN_SECONDS, estimated || READ_ALOUD_MIN_SECONDS);
};

export interface MessageActionBarProps {
  content: string;
  onFeedback?: (value: "up" | "down" | null) => void;
  feedback?: "up" | "down";
  readAloudControl?: ReadAloudControl;
  hideReadAloudControl?: boolean;
}

export const MessageActionBar = ({
  content,
  onFeedback,
  feedback,
  readAloudControl,
  hideReadAloudControl = false,
}: MessageActionBarProps) => {
  const [showCheckMark, setShowCheckMark] = useCopyContent() as [boolean, (value: boolean) => void];
  const [isCopyButtonHovered, setIsCopyButtonHovered] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [hasPlayed, setHasPlayed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const isReadingAloud = readAloudControl?.isActive ?? false;
  const isReadingAloudPaused = readAloudControl?.status === "paused";

  useEffect(() => {
    if (isReadingAloud && !isReadingAloudPaused) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      stopTimer();
      if (!isReadingAloud) {
        setElapsedSeconds(0);
      }
    }
    return stopTimer;
  }, [isReadingAloud, isReadingAloudPaused, stopTimer]);

  useEffect(() => {
    if (isReadingAloud) {
      setHasPlayed(true);
    }
  }, [isReadingAloud]);

  const totalSeconds = useMemo(
    () => readAloudControl?.totalSeconds ?? estimateTotalSeconds(content),
    [content, readAloudControl?.totalSeconds]
  );

  const handleVotePress = (value: "up" | "down") => {
    onFeedback?.(feedback === value ? null : value);
  };

  const handleCopyPress = () => {
    navigator.clipboard.writeText(content);
    setShowCheckMark(true);
  };

  return (
    <View style={styles.container}>
      <AnimatePresence>
        {readAloudControl && !hideReadAloudControl && isReadingAloud && (
          <MotiView
            style={styles.readAloudIndicatorWrap}
            from={{ opacity: 0, translateY: 8, scale: 0.98 }}
            animate={{ opacity: 1, translateY: 0, scale: 1 }}
            exit={{ opacity: 0, translateY: 6, scale: 0.98 }}
            transition={{ type: "timing", duration: 180 }}>
            <ReadAloudIndicator
              audioLevel={readAloudControl.audioLevel}
              audioLevels={readAloudControl.audioLevels}
              elapsedSeconds={readAloudControl.elapsedSeconds}
              totalSeconds={totalSeconds}
              status={readAloudControl.status}
              onPause={readAloudControl.onPause}
              onResume={readAloudControl.onResume}
              showTimer={readAloudControl.showTimer ?? true}
            />
          </MotiView>
        )}
      </AnimatePresence>
      <View style={styles.actionBar}>
        {onFeedback != null && (
          <>
            <Tooltip content="Helpful" placement="bottom" offset={8} showArrow={false}>
              <Pressable
                style={[styles.actionBtn, feedback === "up" && styles.upvoteActive]}
                onPress={() => handleVotePress("up")}
                role="button"
                aria-label="Helpful"
                aria-pressed={feedback === "up"}
                accessibilityRole="button"
                accessibilityLabel="Helpful"
                accessibilityState={{ selected: feedback === "up" }}>
                <View accessibilityElementsHidden accessible={false} importantForAccessibility="no">
                  <ThumbsUpIcon
                    size={16}
                    weight={feedback === "up" ? "fill" : "regular"}
                    color={feedback === "up" ? colors.brand[600] : colors.grey[400]}
                  />
                </View>
              </Pressable>
            </Tooltip>
            <Tooltip content="Not helpful" placement="bottom" offset={8} showArrow={false}>
              <Pressable
                style={[styles.actionBtn, feedback === "down" && styles.downvoteActive]}
                onPress={() => handleVotePress("down")}
                role="button"
                aria-label="Not helpful"
                aria-pressed={feedback === "down"}
                accessibilityRole="button"
                accessibilityLabel="Not helpful"
                accessibilityState={{ selected: feedback === "down" }}>
                <View accessibilityElementsHidden accessible={false} importantForAccessibility="no">
                  <ThumbsDownIcon
                    size={16}
                    weight={feedback === "down" ? "fill" : "regular"}
                    color={feedback === "down" ? colors.error[600] : colors.grey[400]}
                  />
                </View>
              </Pressable>
            </Tooltip>
          </>
        )}

        <Tooltip content={showCheckMark ? "Message copied" : "Copy"} placement="bottom" offset={8} showArrow={false}>
          <View style={styles.copyContainer}>
            {!showCheckMark && (
              <CustomButton
                styleType="transparent"
                size="s"
                onPress={handleCopyPress}
                onHover={setIsCopyButtonHovered}
                aria-label="Copy message"
                accessibilityLabel="Copy message"
                accessibilityHint="Press to copy the message to clipboard"
                role="button"
                style={styles.copyButton}
                leftIcon={<CopyIcon size={16} color={isCopyButtonHovered ? colors.grey[700] : colors.grey[400]} />}
              />
            )}
            {showCheckMark && (
              <View
                style={styles.checkIcon}
                accessible={true}
                accessibilityLabel="Message copied"
                accessibilityLiveRegion="polite">
                <CheckIcon size={16} color={colors.grey[500]} alt="Message copied" role="img" />
              </View>
            )}
          </View>
        </Tooltip>

        {readAloudControl && !hideReadAloudControl && <ReadAloudPlaybackControl readAloudControl={readAloudControl} />}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
  },
  readAloudIndicatorWrap: {
    alignSelf: "flex-start",
  },
  actionBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.xs,
    marginTop: spacing.xxs,
    gap: spacing.xs,
  },
  upvoteActive: {
    backgroundColor: colors.brand[50],
  },
  downvoteActive: {
    backgroundColor: colors.error[100],
  },
  copyContainer: {
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  copyButton: {
    padding: 5,
    width: 28,
    height: 28,
  },
  checkIcon: {
    padding: 5,
    height: 28,
    width: 28,
  },
  actionBtn: {
    padding: spacing.xs,
    borderRadius: radii.md,
  },
});
